/* eslint-disable jsx-a11y/no-autofocus */
import type { DropdownLayoutValueOption, PopoverProps, TooltipProps } from '@wix/design-system';
import { AutoComplete, Box, FormField, listItemSelectBuilder, Text } from '@wix/design-system';
import type { SyntheticEvent } from 'react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { TFunction } from './../../../../ricos-types/commonTypes';
import { useBiNext } from './../../../bi-context-provider/bi-context-provider';
import { EditorBaseUrlContext } from './../../../editor-base-url/editor-base-url-context';
import type { NamedLink } from './../../../link/types';
import { LINK_MODAL_ID } from './../../../link-modal/constants';
import type { SitePagesItem, SitePagesPageType } from './../../../site-pages/types';
import { removeBaseUrl } from './../../../url/remove-base-url';

import { SITE_PAGES_EMPTY_STATE, SITE_PAGES_PAGE_SELECT } from '../../data-hooks/data-hooks';
import type { DropdownOption } from '../types';
import { useSitePages } from './use-site-pages';

type Option = DropdownOption & { page: SitePagesItem };

type Props = {
  autoCompletePopoverProps: PopoverProps;
  autoFocus: boolean;
  link: NamedLink;
  linkType: SitePagesPageType;
  onSelect: (path: string, title: string) => void;
  onSubmit: React.EventHandler<SyntheticEvent>;
  t: TFunction;
  tooltipProps: Partial<TooltipProps>;
};

const typeTranslations: Record<
  | 'AutoComplete_Label'
  | 'AutoComplete_Placeholder'
  | 'EmptyState_Text1'
  | 'EmptyState_Text2'
  | 'NoResults_Any',
  Record<SitePagesPageType, string>
> = {
  AutoComplete_Label: {
    blogPost: 'LinkSettings_SitePages_BlogPost_AutoComplete_Label',
    staticPage: 'LinkSettings_SitePages_StaticPage_AutoComplete_Label',
  },
  AutoComplete_Placeholder: {
    blogPost: 'LinkSettings_SitePages_BlogPost_AutoComplete_Placeholder',
    staticPage: 'LinkSettings_SitePages_StaticPage_AutoComplete_Placeholder',
  },
  EmptyState_Text1: {
    blogPost: 'LinkSettings_SitePages_BlogPost_EmptyState_Text1',
    staticPage: 'LinkSettings_SitePages_StaticPage_EmptyState_Text1',
  },
  EmptyState_Text2: {
    blogPost: 'LinkSettings_SitePages_BlogPost_EmptyState_Text2',
    staticPage: 'LinkSettings_SitePages_StaticPage_EmptyState_Text2',
  },
  NoResults_Any: {
    blogPost: 'LinkSettings_SitePages_BlogPost_NoResults_Any',
    staticPage: 'LinkSettings_SitePages_StaticPage_NoResults_Any',
  },
};

const pageToOption =
  (tooltipProps: Partial<TooltipProps>) =>
  (page: SitePagesItem): Option =>
    Object.assign(
      listItemSelectBuilder({
        ...page,
        ellipsis: true,
        titleMaxLines: 2,
        id: page.id,
        label: page.title,
        size: 'small',
        dataHook: 'option',
        subtitle: page.updatedDate?.toLocaleDateString() || page.description || undefined,
        tooltipProps: {
          ...tooltipProps,
          children: page.title,
          enterDelay: 1_000,
          placement: 'top',
        },
      }),
      { page }
    );

export const SitePages: React.FC<Props> = ({
  autoCompletePopoverProps,
  autoFocus,
  link,
  linkType,
  onSelect,
  onSubmit,
  t,
  tooltipProps,
}) => {
  const { baseUrl } = useContext(EditorBaseUrlContext);
  const logBi = useBiNext({ pluginId: 'link', modalId: LINK_MODAL_ID });
  const pagesState = useSitePages(linkType);
  const options = useMemo<Option[]>(
    () => pagesState.items.map(pageToOption(tooltipProps)),
    [pagesState.items, tooltipProps]
  );

  const [selectedOption, setSelectedOption] = useState<Option | null>(() => {
    const linkPath = removeBaseUrl(link.url || '', baseUrl);
    return options.find(option => option.page.path === linkPath) || null;
  });
  const [value, setValue] = useState<string>(selectedOption?.page.title || '');
  const ref = useRef<AutoComplete>(null);

  const onChange = (value: string) => {
    pagesState.setQuery(value);
    setSelectedOption(null);
    setValue(value);
    onSelect('', '');
  };

  const onSelectOption = (option: DropdownLayoutValueOption) => {
    pagesState.setQuery('');
    setSelectedOption(option as Option);
    setValue((option as Option).page.title);
    onSelect((option as Option).page.path, (option as Option).page.title);
    logBi({ dataHook: SITE_PAGES_PAGE_SELECT, value: (option as Option).page.title });
  };

  const onEnterPressed = (evt: SyntheticEvent) => {
    if (selectedOption) {
      onSubmit(evt);
    } else if ((evt.target as HTMLElement).getAttribute('data-hook')?.includes('option')) {
      setTimeout(() => onSubmit(evt));
    }
  };

  useEffect(() => {
    if (autoFocus) {
      ref.current?.showOptions();
    }
  }, []);

  if (pagesState.areAllFetched && !pagesState.query && !pagesState.items.length) {
    return <EmptyState linkType={linkType} t={t} />;
  }

  return (
    <FormField label={t(typeTranslations.AutoComplete_Label[linkType])} labelPlacement="top">
      <AutoComplete
        autoFocus={autoFocus}
        dataHook={SITE_PAGES_PAGE_SELECT}
        dropdownWidth="auto"
        emptyStateMessage={
          pagesState.isLoading ? undefined : (
            <Text skin="disabled" size="small">
              {pagesState.query
                ? t('LinkSettings_SitePages_All_NoResults_Query', { query: pagesState.query })
                : t(typeTranslations.NoResults_Any[linkType])}
            </Text>
          )
        }
        focusOnSelectedOption
        hasMore={pagesState.hasMore || pagesState.isLoading}
        infiniteScroll
        loadMore={pagesState.loadMore}
        markedOption={selectedOption?.id}
        minWidthPixels="252px"
        onChange={evt => onChange(evt.target.value)}
        onEnterPressed={onEnterPressed}
        onSelect={onSelectOption}
        options={options}
        placeholder={t(typeTranslations.AutoComplete_Placeholder[linkType])}
        popoverProps={autoCompletePopoverProps}
        ref={ref}
        selectedId={selectedOption?.id}
        size="small"
        status={pagesState.error ? 'error' : undefined}
        textOverflow="ellipsis"
        value={value}
      />
    </FormField>
  );
};

const EmptyState: React.FC<{ linkType: SitePagesPageType; t: TFunction }> = ({ linkType, t }) => {
  return (
    <Box
      dataHook={SITE_PAGES_EMPTY_STATE}
      direction="vertical"
      margin="SP4"
      gap="SP2"
      verticalAlign="middle"
      textAlign="center"
    >
      <Text size="small" weight="bold">
        {t(typeTranslations.EmptyState_Text1[linkType])}
      </Text>
      <Text size="small" weight="thin">
        {t(typeTranslations.EmptyState_Text2[linkType])}
      </Text>
    </Box>
  );
};
